/**
 * The knobs this tab is playing on, and the writes that keep them.
 *
 * Settings live on the character, so this is `profileSession` seen from the settings screen's side:
 * it reads the section off whatever profile is live, applies a change the instant it is made, and
 * hands the new record to the same fire-and-forget write a rename goes through. A knob is not a
 * transaction, and blocking a slider on a disk would be worse than the failure it guards against.
 *
 * Nothing is cached here. Every read goes to the live profile, because an import replaces the whole
 * character underneath this object and a copy kept beside it would be the previous character's
 * settings still on screen.
 *
 * `applyAll` is the other half of that: values that came in with an imported profile are not in
 * effect until something puts them there, and the shell calls this on boot and after an import.
 */

import {
  applySettings,
  coerceSetting,
  settingValue,
  withSetting,
} from '@/ui/settings/settingValues';
import type { ProfileSession } from '@/save/profileSession';
import type { SettingDefinition, SettingValue } from '@/ui/settings/settingContract';

export interface SettingSession {
  /** What this knob is on right now, on the character that is live right now. */
  value(definition: SettingDefinition): SettingValue;
  /**
   * Puts the value into effect and writes nothing.
   *
   * What a control calls while it is being dragged. The game changes under the player's hand and
   * the character is left alone until they let go, because a drag is one decision and a write per
   * value passed through on the way is a queue of writes racing to be last.
   */
  apply(definition: SettingDefinition, value: SettingValue): void;
  /** Puts the value into effect and writes it to the character. Does not wait. */
  set(definition: SettingDefinition, value: SettingValue): void;
  /** Puts every registered knob into effect. On boot, and again when an import lands. */
  applyAll(): void;
}

export function createSettingSession(
  profile: ProfileSession,
  definitions: readonly SettingDefinition[],
): SettingSession {
  return {
    value(definition: SettingDefinition): SettingValue {
      return settingValue(definition, profile.current().settings);
    },

    apply(definition: SettingDefinition, value: SettingValue): void {
      definition.apply(coerceSetting(definition, value));
    },

    set(definition: SettingDefinition, value: SettingValue): void {
      const current = profile.current();
      // Coerced rather than trusted, so what the game is doing is what the save says it is doing.
      definition.apply(coerceSetting(definition, value));
      profile.update({ ...current, settings: withSetting(current.settings, definition, value) });
    },

    applyAll(): void {
      applySettings(profile.current().settings, definitions);
    },
  };
}
